'use client';

import { useRef, useState } from 'react';
import type { MedianIncomeYear } from '@/app/(allowed)/median-income/constants';
import { MEDIAN_INCOME_BY_YEAR } from '@/app/(allowed)/median-income/constants';

/**
 * 중위소득 계산기 상태 훅 — 가구원수/월소득(만원)/비율(%) 3-way binding.
 *
 * 입력 필드는 사용자가 타이핑한 raw 문자열을 그대로 보존하고,
 * 그 중 한 필드가 바뀌면 다른 한 필드를 자동으로 다시 계산해 채운다.
 *
 * - mockup의 `lastEdit` 분기와 동일한 효과를 onChange 핸들러에 직접 표현.
 * - percent/amount 모두 `> 0` 일 때만 다른 한 쪽을 채우고, 빈 입력이면 짝꿍 필드도 비운다.
 * - `year` 가 바뀌면 baseline 이 달라지므로, 입력한 월 소득은 유지한 채 percent 만
 *   새 baseline 기준으로 재계산한다 (React 권장 prop-change 추적 패턴).
 */

/** 가구원수 — 1~7(7은 "7인 이상"). householdBaselines 배열의 인덱스 + 1. */
export type HouseholdSize = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** 페이지 진입 시 기본 가구원수(1인). */
const DEFAULT_HOUSEHOLD_SIZE: HouseholdSize = 1;
/** 페이지 진입 시 기본 비율(%). */
const DEFAULT_PERCENT = 100;
/** 만원 단위와 원 단위 사이 변환 계수. */
const WON_PER_MANWON = 10000;

/** 천 단위 구분자 포함 문자열로 포맷. */
const formatWithComma = (n: number) => n.toLocaleString('ko-KR');

/** % 표시용 — 소수 1자리까지, 정수면 정수로. */
const formatPercent = (n: number) => {
  const rounded = Math.round(n * 10) / 10;
  return String(rounded);
};
interface InitialValues {
  householdSize?: HouseholdSize;
  percent?: number;
}
export const useMedianIncomeState = (year: MedianIncomeYear, initialValues?: InitialValues) => {
  const bundle = MEDIAN_INCOME_BY_YEAR[year];
  const {
    householdBaselines
  } = bundle;
  const ascendingTiers = [...bundle.tiers].sort((a, b) => a - b);

  /**
   * 사용자 비율을 ④ 표1의 가장 가까운 상위 구간으로 스냅.
   * - percent ≤ tier 중 가장 작은 tier 를 선택 (예: 105% → 120% 구간, 100% → 100% 구간).
   * - percent 가 모든 tier 보다 크면 가장 큰 tier 를 반환 (예: 300% → 250%).
   * - percent ≤ 0 이면 null (계산 안 됨 상태).
   */
  const findNearestTier = (percent: number): number | null => {
    if (!Number.isFinite(percent) || percent <= 0) return null;
    for (const tier of ascendingTiers) {
      if (percent <= tier) return tier;
    }
    return ascendingTiers[ascendingTiers.length - 1] ?? null;
  };
  const initHousehold = initialValues?.householdSize ?? DEFAULT_HOUSEHOLD_SIZE;
  const initPercent = initialValues?.percent ?? DEFAULT_PERCENT;
  const initialBaselineWon = householdBaselines[initHousehold - 1] ?? 0;
  const initialAmountInput = initialBaselineWon > 0 ? formatWithComma(Math.round(initialBaselineWon * initPercent / 100 / WON_PER_MANWON)) : '';
  const [householdSize, setHouseholdSizeState] = useState<HouseholdSize>(initHousehold);
  //* 사용자가 보는 그대로의 문자열. 만원 단위, "1,234" 같이 콤마 포함.
  const [amountInput, setAmountInput] = useState<string>(initialAmountInput);
  //* 사용자가 보는 그대로의 문자열. % 단위, "107.8" 같이 소수 허용.
  const [percentInput, setPercentInput] = useState<string>(String(initPercent));
  const baselineWon = householdBaselines[householdSize - 1] ?? 0;
  const amountManwon = Number.parseInt(amountInput.replace(/,/g, ''), 10) || 0;

  //* year 변경 감지 → 월 소득은 유지, percent 만 새 baseline 기준으로 재계산
  //  (key/effect 없이 React 권장 prop-change 추적 패턴).
  const previousYearRef = useRef(year);
  // eslint-disable-next-line react-hooks/refs -- React 권장 prop-change 추적 패턴(렌더 중 이전 year 비교)
  if (previousYearRef.current !== year) {
    // eslint-disable-next-line react-hooks/refs -- 위와 동일, 렌더 중 이전 year 기록
    previousYearRef.current = year;
    if (amountManwon > 0 && baselineWon > 0) {
      setPercentInput(formatPercent(amountManwon * WON_PER_MANWON / baselineWon * 100));
    } else {
      setPercentInput('');
    }
  }
  const percent_0 = Number.parseFloat(percentInput) || 0;
  const tier_0 = findNearestTier(percent_0);

  /** 가구원수 변경 — percent 가 있으면 percent 유지하고 amount 재계산, 없으면 amount 유지하고 percent 재계산. */
  const setHouseholdSize = (next: HouseholdSize) => {
    setHouseholdSizeState(next);
    const nextBaselineWon = householdBaselines[next - 1] ?? 0;
    if (percent_0 > 0 && nextBaselineWon > 0) {
      setAmountInput(formatWithComma(Math.round(nextBaselineWon * percent_0 / 100 / WON_PER_MANWON)));
      return;
    }
    if (amountManwon > 0 && nextBaselineWon > 0) {
      setPercentInput(formatPercent(amountManwon * WON_PER_MANWON / nextBaselineWon * 100));
    }
  };

  /** 월 소득(만원) 입력 변경 — Input(type='local-string-number')에서 숫자만 남긴 문자열이 들어옴. */
  const onAmountChange = (rawDigits: string) => {
    if (!rawDigits) {
      setAmountInput('');
      setPercentInput('');
      return;
    }
    const n = Number.parseInt(rawDigits, 10) || 0;
    setAmountInput(formatWithComma(n));
    if (n > 0 && baselineWon > 0) {
      setPercentInput(formatPercent(n * WON_PER_MANWON / baselineWon * 100));
    } else {
      setPercentInput('');
    }
  };

  /** 중위소득 비율(%) 입력 변경 — 사용자가 직접 친 raw 문자열 그대로 받음(소수점 가능). */
  const onPercentChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '');
    setPercentInput(cleaned);
    if (!cleaned) {
      setAmountInput('');
      return;
    }
    const n_0 = Number.parseFloat(cleaned);
    if (Number.isFinite(n_0) && n_0 > 0 && baselineWon > 0) {
      setAmountInput(formatWithComma(Math.round(baselineWon * n_0 / 100 / WON_PER_MANWON)));
    } else {
      setAmountInput('');
    }
  };
  return {
    /** 가구원수 (1~7). 7은 "7인 이상" 으로 간주. */
    householdSize,
    /** 비율(%) 파싱값. 입력 비어있으면 0. */
    percent: percent_0,
    /** 월 소득(만원) 파싱값. 입력 비어있으면 0. */
    amountManwon,
    /** ④ 표의 가까운 구간. percent ≤ 0 이면 null. */
    tier: tier_0,
    /** 만원 input 표시값. comma 포맷 포함. */
    amountInput,
    /** % input 표시값. */
    percentInput,
    /** 현재 가구원수의 100% 기준 월 금액(원 단위). */
    baselineWon,
    setHouseholdSize,
    onAmountChange,
    onPercentChange
  };
};
export type MedianIncomeState = ReturnType<typeof useMedianIncomeState>;